import { Checkbox, Field, RadioGroup, Select } from '@bb/ui';
import type { CanvasEntryControls } from '../../../api/client.ts';
import { DurationField } from '../../../components/DurationField.tsx';
import { AudienceBuilder, emptyAudience } from '../../audience/AudienceBuilder.tsx';
import { NumberInput } from '../../campaigns/trigger/NumberInput.tsx';
import type { WizardStepProps } from './EntryStep.tsx';
import { LIMIT_PERIOD_LABELS, LIMIT_PERIODS, messagesAt, triggerFilterOverlap, type EntryType, type SettingsIssue } from './settings.ts';

const WHO = [
  { value: 'everyone', label: 'All users' },
  { value: 'audience', label: 'Users who match an audience' },
];

/** Re-entry, several entries at once and the entry limit (BB-111), each with what it does. */
export function EntryControlsForm({ controls, entryType, issues, readOnly, onChange }: { controls: CanvasEntryControls; entryType: EntryType | null; issues: SettingsIssue[]; readOnly: boolean; onChange: (next: CanvasEntryControls) => void }) {
  const periods = LIMIT_PERIODS[entryType ?? 'action_based'];
  const { reentry, limit } = controls;
  return (
    <fieldset className="audience-group" aria-label="Entry controls">
      <legend>Entry controls</legend>
      <div>
        <Checkbox label="Allow users to re-enter this Canvas" checked={reentry.enabled} disabled={readOnly} onCheckedChange={(enabled) => onChange({ ...controls, reentry: { ...reentry, enabled } })} />
        <p className="hint">Off: each user enters once, ever. On: a user can enter again once the window has passed since their last entry.</p>
        {reentry.enabled && (
          <DurationField
            label="Re-entry window"
            seconds={reentry.windowSeconds}
            units={['minutes', 'hours', 'days']}
            disabled={readOnly}
            hint="0 lets a user enter again as soon as their previous entry allows it."
            error={messagesAt(issues, 'controls', 'reentry', 'windowSeconds')[0]}
            onChange={(windowSeconds) => onChange({ ...controls, reentry: { ...reentry, windowSeconds } })}
          />
        )}
      </div>
      <div>
        <Checkbox label="Allow several entries of a user at the same time" checked={controls.allowConcurrent} disabled={readOnly} onCheckedChange={(allowConcurrent) => onChange({ ...controls, allowConcurrent })} />
        <p className="hint">Off: a user still in the Canvas cannot enter again until they finish. On: each trigger starts its own pass, for example one per transaction; exit correlation then ends the right one.</p>
        {controls.allowConcurrent && !reentry.enabled && <p className="notice notice--neutral">Several entries at once need re-entry: without it a user enters only once.</p>}
      </div>
      <div>
        <Checkbox
          label="Limit entrance volume"
          checked={limit !== undefined}
          disabled={readOnly}
          onCheckedChange={(on) => {
            const { limit: _old, ...rest } = controls;
            onChange(on ? { ...rest, limit: { count: 1000, per: periods.includes('day') ? 'day' : 'run' } } : rest);
          }}
        />
        <p className="hint">At most this many entries in the period; entries over the limit are refused. Hours and days are counted in UTC.</p>
        {limit && (
          <div className="audience-filter">
            <Field label="Entries at most" error={messagesAt(issues, 'controls', 'limit', 'count')[0]}>
              <NumberInput min={1} step={1} value={limit.count > 0 ? limit.count : undefined} disabled={readOnly} onValueChange={(count) => onChange({ ...controls, limit: { ...limit, count: count ?? 0 } })} />
            </Field>
            <Field label="Period" error={messagesAt(issues, 'controls', 'limit', 'per')[0]}>
              <Select options={periods.map((p) => ({ value: p, label: LIMIT_PERIOD_LABELS[p] }))} value={limit.per} disabled={readOnly} onChange={(e) => onChange({ ...controls, limit: { ...limit, per: e.target.value as typeof limit.per } })} />
            </Field>
          </div>
        )}
      </div>
    </fieldset>
  );
}

/**
 * Target of a Canvas (BB-111): who may enter (checked once, at entry) and the entry controls. Warns when an action
 * trigger is also an audience filter.
 */
export function TargetStep({ environmentId, settings, onChange, issues, readOnly }: WizardStepProps) {
  const overlap = triggerFilterOverlap(settings);
  return (
    <section aria-label="Target" className="canvas-wizard__panel">
      <h2>Target audience</h2>
      <RadioGroup
        label="Who can enter"
        options={WHO.map((o) => ({ ...o, disabled: readOnly }))}
        value={settings.audience ? 'audience' : 'everyone'}
        onValueChange={(who) => onChange({ ...settings, audience: who === 'audience' ? (settings.audience ?? emptyAudience()) : null })}
      />
      <p className="hint">The audience is checked once, when a user enters. Users who stop matching it later stay in the Canvas; use exit criteria to take them out.</p>
      {overlap.length > 0 && (
        <p className="notice notice--warning" role="status">
          {overlap.map((name) => `«${name}»`).join(', ')} {overlap.length === 1 ? 'enters users and is also' : 'enter users and are also'} in the audience filters. The audience is checked after the triggering action is recorded, so that action already counts: a filter like «has not done it» would let nobody in.
        </p>
      )}
      {messagesAt(issues, 'audience').map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
      {settings.audience && <AudienceBuilder environmentId={environmentId} value={settings.audience} readOnly={readOnly} onChange={(audience) => onChange({ ...settings, audience })} />}
      <EntryControlsForm controls={settings.controls} entryType={settings.entry?.type ?? null} issues={issues} readOnly={readOnly} onChange={(controls) => onChange({ ...settings, controls })} />
    </section>
  );
}
